import React, { useState, useEffect } from 'react';
import CharacterTagOverlay from './CharacterTagOverlay';
import CustomModal from './CustomModal';
import { API_ENDPOINTS } from '../config/backendConfig';

const ArtworkInfoModal = ({ 
  isOpen, 
  onClose, 
  artworkUrl, 
  onSceneGenerated, 
  currentUser,
  onTaggedCharactersSaved // Add this new prop
}) => {
  console.log('=== ARTWORK INFO MODAL RENDER ===');
  console.log('isOpen:', isOpen);
  console.log('artworkUrl:', artworkUrl);
  console.log('currentUser:', currentUser);
  
  const [artworkType, setArtworkType] = useState('people');
  const [artworkCharacters, setArtworkCharacters] = useState([]);
  const [artworkObject, setArtworkObject] = useState({ name: '', brief: '' });
  const [sceneAction, setSceneAction] = useState('');
  const [firstSceneLoading, setFirstSceneLoading] = useState(false);
  const [firstSceneError, setFirstSceneError] = useState(null);
  // Remove taggedCharacters state
  // const [taggedCharacters, setTaggedCharacters] = useState([]);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [peopleTags, setPeopleTags] = useState([]);
  const [objectTags, setObjectTags] = useState([]);
  const [worldTags, setWorldTags] = useState([]);

  // Handle tag position changes when dragging
  const handleTagPositionChange = (tagId, newX, newY) => {
    console.log('=== TAG POSITION CHANGE ===');
    console.log('tagId:', tagId);
    console.log('newX:', newX, 'newY:', newY);
    
    // Update the correct tag array based on tag type
    if (artworkType === 'people') {
      setPeopleTags(prevTags => 
        prevTags.map(tag => 
          tag.id === tagId 
            ? { ...tag, x: newX, y: newY }
            : tag
        )
      );
    } else if (artworkType === 'object') {
      setObjectTags(prevTags => 
        prevTags.map(tag => 
          tag.id === tagId 
            ? { ...tag, x: newX, y: newY }
            : tag
        )
      );
    } else if (artworkType === 'world') {
      setWorldTags(prevTags => 
        prevTags.map(tag => 
          tag.id === tagId 
            ? { ...tag, x: newX, y: newY }
            : tag
        )
      );
    }
  };

  // Auto-generate tags when characters are added/updated
  useEffect(() => {
    console.log('=== TAG GENERATION EFFECT ===');
    console.log('artworkType:', artworkType);
    console.log('artworkCharacters:', artworkCharacters);
    
    if (artworkType === 'people' && artworkCharacters.length > 0) {
      const validCharacters = artworkCharacters.filter(char => char.name.trim());
      console.log('validCharacters:', validCharacters);
      
      const positions = [
        { x: 30, y: 30 },
        { x: 70, y: 30 },
        { x: 30, y: 70 },
        { x: 70, y: 70 },
        { x: 50, y: 50 },
      ];
      // Preserve existing tag positions and only create new tags for new characters
      const existingTags = peopleTags.filter(tag => 
        validCharacters.some(char => char.name === tag.name)
      );
      const newTags = validCharacters
        .filter(char => !existingTags.some(tag => tag.name === char.name))
        .map((char, index) => {
          const existingTagCount = existingTags.length;
          const positionIndex = (existingTagCount + index) % positions.length;
          return {
            id: `tag_${Date.now()}_${index}`,
            type: 'people',
            name: char.name,
            age: char.age,
            gender: char.gender,
            appearance: char.appearance,
            x: positions[positionIndex].x,
            y: positions[positionIndex].y,
            isPlaced: true
          };
        });
      // Update existing tags with new character details but preserve positions
      const updatedExistingTags = existingTags.map(tag => {
        const char = validCharacters.find(c => c.name === tag.name);
        if (char) {
          return {
            ...tag,
            age: char.age,
            gender: char.gender,
            appearance: char.appearance
          };
        }
        return tag;
      });
      const allTags = [...updatedExistingTags, ...newTags];
      console.log('Generated tags:', allTags);
      setPeopleTags(allTags);
      if (artworkType === 'people') {
        // setTaggedCharacters(allTags); // This line is removed
      }
    } else if (artworkType === 'people') {
      console.log('Clearing people tags');
      setPeopleTags([]);
      // setTaggedCharacters([]); // This line is removed
    }
  }, [artworkCharacters, artworkType]);

  // Auto-generate tag for object
  useEffect(() => {
    console.log('=== OBJECT TAG GENERATION EFFECT ===');
    console.log('artworkType:', artworkType);
    console.log('artworkObject:', artworkObject);
    
    if (artworkType === 'object' && artworkObject.name.trim()) {
      setObjectTags(prev => {
        // Check if object tag already exists
        const existingTag = prev.find(tag => tag.objectName === artworkObject.name);
        
        if (!existingTag) {
          const newTag = [{
            id: `tag_object_${Date.now()}`,
            type: 'object',
            objectName: artworkObject.name,
            objectBrief: artworkObject.brief,
            x: 50,
            y: 50,
            isPlaced: true
          }];
          console.log('Generated new object tag:', newTag);
          return newTag;
        } else {
          // Update existing tag
          const updatedTag = prev.map(tag => 
            tag.objectName === artworkObject.name 
              ? { ...tag, objectBrief: artworkObject.brief }
              : tag
          );
          console.log('Updated existing object tag:', updatedTag);
          return updatedTag;
        }
      });
    } else if (artworkType === 'object') {
      console.log('Clearing object tags');
      setObjectTags([]);
    }
  }, [artworkType, artworkObject]);

  const handleGenerateScene = async () => {
    console.log('=== HANDLE GENERATE SCENE STARTED ===');
    console.log('artworkType:', artworkType);
    console.log('peopleTags:', peopleTags);
    console.log('objectTags:', objectTags);
    console.log('worldTags:', worldTags);
    
    setFirstSceneLoading(true);
    setFirstSceneError(null);
    try {
      let characterList = [];
      let scenePrompt = '';
      if (artworkType === 'people') {
        characterList = artworkCharacters;
        scenePrompt = `This artwork is about these characters: ` + artworkCharacters.map(c => `${c.name} (age ${c.age}, gender ${c.gender}, appearance: ${c.appearance})`).join('; ') + `. Scene: ${sceneAction}`;
      } else if (artworkType === 'object') {
        characterList = [{ type: 'object', name: artworkObject.name, brief: artworkObject.brief }];
        scenePrompt = `This artwork is about an object called ${artworkObject.name}. Reason: ${artworkObject.brief}. Scene: ${sceneAction}`;
      }
      
      // First, process tags for image cropping if there are any tags
      const allTags = [...peopleTags, ...objectTags];
      console.log('=== TAG PROCESSING CHECK ===');
      console.log('allTags length:', allTags.length);
      console.log('allTags:', allTags);
      
      // Process tags if there are any
      let processedTagsFromBackend = null;
      if (allTags.length > 0) {
        console.log('=== PROCESSING TAGS ===');
        try {
          const tagResponse = await fetch(API_ENDPOINTS.processTags, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              storyId: 'test-story-id',
              userId: currentUser?.uid || 'anonymous',
              artworkUrl: artworkUrl,
              tags: allTags,
              timestamp: new Date().toISOString()
            }),
          });
          if (tagResponse.ok) {
            const tagData = await tagResponse.json();
            console.log('✅ Tags processed successfully:', tagData);
            if (tagData.processedTags && tagData.processedTags.length > 0) {
              processedTagsFromBackend = tagData.processedTags;
              console.log('Using processedTags from backend for taggedCharacters:', JSON.stringify(processedTagsFromBackend, null, 2));
            }
          } else {
            console.warn('⚠️ Tag processing failed, continuing without tags');
          }
        } catch (tagError) {
          console.warn('⚠️ Tag processing error, continuing without tags:', tagError);
        }
      }
      
      // Call backend to generate the first scene
      console.log('=== CALLING SCENE GENERATION API ===');
      console.log('API endpoint:', API_ENDPOINTS.generateStoryScene);
      console.log('Request payload:', {
        imageUrl: artworkUrl,
        title: 'My Story',
        prompt: scenePrompt,
        artistName: currentUser?.displayName || 'Young Artist',
        sceneNumber: 1,
        userId: currentUser?.uid || 'anonymous',
        characters: characterList,
        sceneAction: sceneAction,
      });
      
      const response = await fetch(API_ENDPOINTS.generateStoryScene, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: artworkUrl,
          title: 'My Story',
          prompt: scenePrompt,
          artistName: currentUser?.displayName || 'Young Artist',
          sceneNumber: 1,
          userId: currentUser?.uid || 'anonymous',
          characters: characterList,
          sceneAction: sceneAction,
        }),
      });
      
      console.log('=== RESPONSE RECEIVED ===');
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);
        throw new Error(errorData.error || 'Failed to generate scene');
      }
      
      const data = await response.json();
      console.log('✅ Scene generated successfully:', data);
      console.log('✅ Full response data:', JSON.stringify(data, null, 2));
      
      // Check if we have a valid scene response
      if (!data.scene) {
        console.error('❌ No scene data in response:', data);
        throw new Error('Invalid response: No scene data received from AI');
      }
      
      // Add the generated scene
      const newScene = {
        title: data.scene.title || 'Scene 1',
        description: data.scene.description || 'No description generated',
        imageUrl: artworkUrl,
        characters: data.scene.characters || characterList,
        taggedCharacters: processedTagsFromBackend && processedTagsFromBackend.length > 0 ? processedTagsFromBackend : allTags, // Use processedTags if present
        setting: data.scene.setting || '',
        mood: data.scene.mood || '',
      };
      
      console.log('✅ Created new scene object:', newScene);
      
      // Automatically save tagged characters when scene is generated
      if (onTaggedCharactersSaved && allTags.length > 0) {
        onTaggedCharactersSaved(allTags);
      }
      
      onSceneGenerated(newScene);
      onClose();
    } catch (err) {
      console.error('Error in handleGenerateScene:', err);
      setFirstSceneError(err.message);
    } finally {
      setFirstSceneLoading(false);
    }
  };

  // Test function to debug scene generation
  const handleTestSceneGeneration = async () => {
    console.log('=== TEST SCENE GENERATION ===');
    setFirstSceneLoading(true);
    setFirstSceneError(null);
    
    try {
      const testResponse = await fetch(API_ENDPOINTS.testSceneGeneration, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: artworkUrl,
          title: 'Test Story',
          technique: 'Test Technique',
          inspiration: 'Test Inspiration',
          artistName: currentUser?.displayName || 'Young Artist'
        }),
      });
      
      console.log('Test response status:', testResponse.status);
      const testData = await testResponse.json();
      console.log('Test response data:', testData);
      
      if (testResponse.ok && testData.success) {
        setModal({
          isOpen: true,
          title: "✅ Test Successful",
          message: `Scene generation test successful! Scene title: ${testData.data.scene.title}`,
          type: 'success'
        });
      } else {
        throw new Error(testData.error || 'Test failed');
      }
    } catch (err) {
      console.error('Test error:', err);
      setFirstSceneError(`Test failed: ${err.message}`);
    } finally {
      setFirstSceneLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-amber-200">
            <h3 className="text-xl font-bold text-amber-800">Tell Artmosphere about your artwork</h3>
            <button
              className="text-gray-500 hover:text-gray-700 text-2xl"
              onClick={onClose}
              title="Close"
            >
              ×
            </button>
          </div>
          
          <div className="flex flex-col lg:flex-row h-full" style={{ height: 'calc(95vh - 100px)' }}>
            {/* Left: Artwork */}
            <div className="lg:w-1/2 p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-amber-800 mb-2">
                  🎨 Your Artwork
                </h4>
                <p className="text-sm text-amber-600">
                  This is your artwork. Add character details on the right, then tag them on the artwork!
                </p>
              </div>
              
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl overflow-hidden relative" style={{ aspectRatio: '1', maxHeight: '60vh' }}>
                <img
                  src={artworkUrl}
                  alt="Your Artwork"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log('Artwork failed to load in modal');
                    e.target.style.display = 'none';
                  }}
                />
                {/* Always show all tags directly on the artwork */}
                <CharacterTagOverlay
                  artworkUrl={artworkUrl}
                  taggedCharacters={[...peopleTags, ...objectTags]}
                  showTags={true}
                  draggable={true}
                  onTagPositionChange={handleTagPositionChange}
                  className="absolute inset-0 border-4 border-red-500 z-50"
                />
              </div>
            </div>

            {/* Right: Character forms */}
            <div className="lg:w-1/2 p-6 bg-amber-50 border-l border-amber-200 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 100px)' }}>
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-amber-800 mb-2">
                  👥 Define Your Characters
                </h4>
                <p className="text-sm text-amber-600">
                  Add details about the characters in your artwork. Character tags will automatically appear on the artwork as you type! When you generate a scene, all character details and positions will be saved automatically.
                </p>
              </div>

              {/* Character type selection */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-amber-800 mb-2">
                  What is this artwork about?
                </label>
                <div className="flex gap-2 mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="artworkType"
                      value="people"
                      checked={artworkType === 'people'}
                      onChange={() => setArtworkType('people')}
                    />
                    Characters
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="artworkType"
                      value="object"
                      checked={artworkType === 'object'}
                      onChange={() => setArtworkType('object')}
                    />
                    Scene Items
                  </label>
                </div>
              </div>

              {/* Character forms based on type */}
              {artworkType === 'people' && (
                <div className="space-y-4">
                  <div className="text-amber-700 font-semibold mb-2">
                    🧑‍🎨 Let's add your characters! You can add as many as you want. Give them fun names and tell us about their clothes or what makes them special! ✨
                  </div>
                  {artworkCharacters.map((char, idx) => (
                    <div key={idx} className="bg-white border-2 border-amber-200 rounded-xl p-4 relative">
                      {artworkCharacters.length > 1 && (
                        <button
                          className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-lg font-bold"
                          onClick={() => setArtworkCharacters(artworkCharacters.filter((_, i) => i !== idx))}
                          title="Remove Character"
                        >
                          ×
                        </button>
                      )}
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-amber-800">Character Name</label>
                        <input 
                          type="text" 
                          className="w-full border rounded p-2" 
                          value={char.name} 
                          onChange={e => {
                            const updated = [...artworkCharacters];
                            updated[idx].name = e.target.value;
                            setArtworkCharacters(updated);
                          }} 
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-amber-800">Age</label>
                        <input 
                          type="number" 
                          className="w-full border rounded p-2" 
                          value={char.age} 
                          onChange={e => {
                            const updated = [...artworkCharacters];
                            updated[idx].age = e.target.value;
                            setArtworkCharacters(updated);
                          }} 
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-amber-800">Gender</label>
                        <input 
                          type="text" 
                          className="w-full border rounded p-2" 
                          value={char.gender} 
                          onChange={e => {
                            const updated = [...artworkCharacters];
                            updated[idx].gender = e.target.value;
                            setArtworkCharacters(updated);
                          }} 
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-semibold text-amber-800">
                          Clothes & Appearance 
                          <span className="text-xs text-amber-600"> (What are they wearing? Any cool features?)</span>
                        </label>
                        <input 
                          type="text" 
                          className="w-full border rounded p-2" 
                          value={char.appearance} 
                          onChange={e => {
                            const updated = [...artworkCharacters];
                            updated[idx].appearance = e.target.value;
                            setArtworkCharacters(updated);
                          }} 
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    className="w-full bg-amber-200 hover:bg-amber-300 text-amber-800 font-bold py-2 px-4 rounded-xl shadow transition-all duration-300"
                    onClick={() => setArtworkCharacters([...artworkCharacters, { name: '', age: '', gender: '', appearance: '' }])}
                  >
                    + Add Another Character
                  </button>
                </div>
              )}

              {artworkType === 'object' && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-amber-800">Object Name</label>
                  <input 
                    type="text" 
                    className="w-full border rounded p-2" 
                    value={artworkObject.name} 
                    onChange={e => setArtworkObject({ ...artworkObject, name: e.target.value })} 
                  />
                  <label className="block text-sm font-semibold text-amber-800">Why did you draw this object?</label>
                  <textarea 
                    className="w-full border rounded p-2" 
                    value={artworkObject.brief} 
                    onChange={e => setArtworkObject({ ...artworkObject, brief: e.target.value })} 
                  />
                </div>
              )}

              {/* Scene Action/Description for all types */}
              <div className="mt-6">
                <label className="block text-sm font-semibold text-amber-800 mb-2">
                  Let the author in you write your scene for this image?
                  <span className="text-xs text-amber-600"> think about the story behind the scene, basis the characters , scene elements identfied for this scene</span>
                </label>
                <textarea
                  className="w-full border rounded p-2"
                  value={sceneAction}
                  onChange={e => setSceneAction(e.target.value)}
                  placeholder="E.g. The princess is dancing at the castle party, the dragon is flying over the mountains, etc."
                  rows="3"
                />
              </div>

              {/* Action buttons */}
              <div className="mt-6 pb-4 space-y-3">
                {/* Test Scene Generation button */}
                <button
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-400 text-white font-semibold py-2 px-4 rounded-xl shadow hover:from-blue-600 hover:to-blue-500 transition-all duration-300"
                  onClick={handleTestSceneGeneration}
                  disabled={firstSceneLoading}
                >
                  {firstSceneLoading ? 'Testing...' : '🧪 Test Scene Generation'}
                </button>

              {/* Generate Scene button */}
                <button
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-400 text-white font-semibold py-3 px-4 rounded-xl shadow hover:from-amber-600 hover:to-amber-500 transition-all duration-300"
                  onClick={handleGenerateScene}
                  disabled={firstSceneLoading || !artworkType || (artworkType === 'people' && artworkCharacters.some(c => !c.name)) || (artworkType === 'object' && !artworkObject.name) || !sceneAction}
                >
                  {firstSceneLoading ? 'Generating Scene...' : '🎭 Generate Scene'}
                </button>
                {/* Removed Generate Artwork button for improved UI aesthetics */}
                {firstSceneError && (
                  <div className="text-red-600 text-sm mt-2">{firstSceneError}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Modal for success messages */}
      <CustomModal
        isOpen={modal.isOpen}
        onClose={() => {
          setModal({ isOpen: false, title: '', message: '', type: 'info' });
        }}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
    </>
  );
};

export default ArtworkInfoModal; 